import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    CcReviewComplianceHistory,
    CcReviewComplianceHistoryImportProps,
    CcReviewComplianceItemState,
    CcReviewComplianceRevision,
    CcReviewComplianceTempAnswer,
    CcReviewComplianceTempAnswerVersion,
    CcLibrarySet,
} from "server/models"
import {
    IDLE, PENDING, EDIT, NO,
    WORKFLOW_RERUN,
    WORKFLOW_APPROVED,
    DESC,
} from "const"
import { StateType, CcStateSettingsType, hasProjectAccess } from "client/utils"
import { sort } from "client/store/utils"
import {
    PRM_CC_REVIEW,
    StoreType,
    ReviewComplianceRevisionValidation,

    LoadCcReviewComplianceRevisionData,
    LoadCcReviewComplianceRevisions,
    LoadAllCcReviewComplianceItemState,
    LoadAllCcReviewComplianceHistory,

    CreateCcReviewComplianceState,
    ImportCcReviewComplianceHistory,
    CreateCcReviewComplianceRevision,
    UpdateCcReviewComplianceRevision,
    SaveCcReviewComplianceTempAnswer,
    SaveCcReviewComplianceTempAnswerVersions,

    DeleteCcReviewComplianceRevision,

    setCcReviewComplianceRevisionList,
    setCcReviewComplianceRevisionActive,
    setCcReviewComplianceItemLoadState,

    initReviewComplianceHistory,
} from "client/store"
import {
    TableCustomLevelViewProps,

    SelectDropdownItem,
    FormGroup,
    Label,
    Input,
    Feedback,
    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,

    PrepareNestedHistory,
} from "client/components"

import {
    SetDataWrapper,
    SetName,
    SelectDropdownStyled,
    ConfirmTable,
} from "client/pages/cc/setCustomViewStyled"


const SetCustomView = ({
    groupData,
}: TableCustomLevelViewProps) => {
    const set = groupData as CcLibrarySet

    const activeVersion = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)
    const revisionList = useSelector((state: StoreType) => state.ccReviewCompliance.revisionList)
    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewCompliance.revisionActiveList)

    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const reviewLevelIds = moduleLevelList[2] || []
    const formalLevelIds = moduleLevelList[0] || []
    const levelIds = set && set.formal ? formalLevelIds : reviewLevelIds

    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [revisions, setRevisions] = useState<CcReviewComplianceRevision[]>([])

    const [showDelete, setShowDelete] = useState(false)
    const [showRename, setShowRename] = useState(false)
    const [showRerun, setShowRerun] = useState(false)

    const [revisionName, setRevisionName] = useState("")
    const [revisionValidation, setRevisionValidation] = useState<ReviewComplianceRevisionValidation>(null)
    const [renameState, setRenameState] = useState<StateType>(IDLE)

    const activeRevision = (revisionActiveList[activeVersion.id] && revisionActiveList[activeVersion.id][set.id]) || null
    const editAccess = hasProjectAccess(PRM_CC_REVIEW, EDIT)

    useEffect(() => {
        // display the last revision on top
        const revisionArray: CcReviewComplianceRevision[] = revisionList[set.id] ? revisionList[set.id].slice() : []
        revisionArray.sort((a, b) => sort(a, b, "number", DESC))
        setRevisions(revisionArray)
    }, [revisionList])

    const changeActiveRevision = (revision: CcReviewComplianceRevision) => {
        dispatch(setCcReviewComplianceItemLoadState(PENDING))
        dispatch(setCcReviewComplianceRevisionActive(activeVersion.id, set.id, revision))

        LoadCcReviewComplianceRevisionData(revision, dispatch).then(() => {
            dispatch(setCcReviewComplianceItemLoadState(IDLE))
        })
    }

    const onCancelDelete = () => {
        setShowDelete(false)
    }
    const onConfirmDelete = () => {
        // on removing the last revision - it will be created again
        // set can not be removed from the list as it is used in other file/version pairs
        setShowDelete(false)
        dispatch(setCcReviewComplianceItemLoadState(PENDING))

        DeleteCcReviewComplianceRevision(activeRevision.id).then(() => {
            LoadCcReviewComplianceRevisions(project.id, activeVersion.fileId, activeVersion.id, set.id)
            .then((newRevisions) => {
                if (newRevisions.length > 0) {
                    // set new revision list and load revision data
                    const lastRevision = newRevisions[newRevisions.length - 1]
                    dispatch(setCcReviewComplianceRevisionList(set.id, newRevisions))
                    dispatch(setCcReviewComplianceRevisionActive(activeVersion.id, set.id, lastRevision))

                    LoadCcReviewComplianceRevisionData(lastRevision, dispatch).then(() => {
                        dispatch(setCcReviewComplianceItemLoadState(IDLE))
                    })
                } else {
                    // force - create new revision
                    CreateCcReviewComplianceRevision(project.id, activeVersion.fileId, activeVersion.id, set.id)
                    .then((newRevision) => {
                        dispatch(setCcReviewComplianceRevisionList(set.id, [newRevision]))
                        dispatch(setCcReviewComplianceRevisionActive(activeVersion.id, set.id, newRevision))

                        LoadCcReviewComplianceRevisionData(newRevision, dispatch).then(() => {
                            dispatch(setCcReviewComplianceItemLoadState(IDLE))
                        })
                    })
                }
            })
        })
    }

    const onCancelRename = () => {
        setShowRename(false)
    }
    const onConfirmRename = () => {
        setRenameState(PENDING)
        UpdateCcReviewComplianceRevision(activeRevision.id, revisionName, lang).then((validation) => {
            if (validation) {
                setRevisionValidation(validation)
                setRenameState(IDLE)
            } else {
                LoadCcReviewComplianceRevisions(project.id, activeVersion.fileId, activeVersion.id, set.id).then((newRevisions) => {
                    const newActiveRevision = newRevisions.find((row) => row.id === activeRevision.id)
                    dispatch(setCcReviewComplianceRevisionList(set.id, newRevisions))
                    dispatch(setCcReviewComplianceRevisionActive(activeVersion.id, set.id, newActiveRevision))

                    setRenameState(IDLE)
                    setShowRename(false)
                })
            }
        })
    }

    const onCancelRerun = () => {
        setShowRerun(false)
    }
    const onRerun = (rerunAll: boolean) => {
        setShowRerun(false)
        dispatch(setCcReviewComplianceItemLoadState(PENDING))

        LoadCcReviewComplianceRevisions(project.id, activeVersion.fileId, activeVersion.id, set.id).then((resRevisions) => {
            CreateCcReviewComplianceRevision(project.id, activeVersion.fileId, activeVersion.id, set.id)
            .then((newRevision) => {
                const lastRevision = resRevisions[resRevisions.length - 1]

                Promise.all([
                    LoadAllCcReviewComplianceItemState(lastRevision.id),
                    LoadAllCcReviewComplianceHistory(lastRevision.id),
                ]).then(([stateList, historyList]) => {
                    const promiseArray: Promise<any>[] = []

                    Object.keys(historyList).forEach((index) => {
                        const itemId = Number(index)
                        const itemHistory = historyList[itemId] || []
                        const itemState = stateList[itemId]
                        const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}

                        let lastHistory: CcReviewComplianceHistory = null
                        let lastResponsibleAnswer: CcReviewComplianceHistory = null
                        for (let i = itemHistory.length - 1; i >= 0; i--) {
                            const history = itemHistory[i]
                            if (history.workflow === WORKFLOW_RERUN) {
                                break
                            }
                            if (!history.systemMessage) {
                                if (!lastHistory) lastHistory = history
                                if (!lastResponsibleAnswer && history.parentId === null) lastResponsibleAnswer = history
                            }
                            if (lastHistory && lastResponsibleAnswer) break
                        }

                        let rerun = false
                        if (rerunAll) {
                            // rerun without condition
                            rerun = true
                        } else if (lastHistory && lastHistory.responseId && lastHistory.workflow === WORKFLOW_APPROVED) {
                            // check only answers by the last verifier level, rerun if not compliant
                            const response = responseList[lastHistory.responseId] || null
                            if (response && response.approved === NO) {
                                rerun = true
                            }
                        }

                        let newState: CcReviewComplianceItemState = {
                            ...itemState,
                            reviewComplianceRevisionId: newRevision.id,
                        }

                        // reassign item to first level only if rerun, otherwise - just copy item state and history
                        if (rerun) {
                            const newHistory: CcReviewComplianceHistory = {
                                ...initReviewComplianceHistory,
                                id: null,
                                itemId,
                                reviewComplianceRevisionId: newRevision.id,
                                parentId: null,
                                workflow: WORKFLOW_RERUN,
                                systemMessage: true,
                                editorId: userData.userId,
                            }
                            newState = {
                                ...newState,
                                levelId: levelIds[0] || null,
                                verifierId: (levelIds[0] && settings[levelIds[0]]) || null,
                            }

                            itemHistory.push(newHistory)

                            if (lastResponsibleAnswer) {
                                const tempAnswer: CcReviewComplianceTempAnswer = {
                                    itemId,
                                    reviewComplianceRevisionId: newRevision.id,
                                    editorId: lastResponsibleAnswer.prevUserId,
                                    responseId: lastResponsibleAnswer.responseId,
                                    answer: lastResponsibleAnswer.answer,
                                    remark: lastResponsibleAnswer.remark,
                                    nextUserId: lastResponsibleAnswer.nextUserId,
                                }
                                const tempVersions: CcReviewComplianceTempAnswerVersion[] = []
                                lastResponsibleAnswer.versions.forEach((version) => {
                                    tempVersions.push({
                                        itemId,
                                        reviewComplianceRevisionId: newRevision.id,
                                        editorId: lastResponsibleAnswer.prevUserId,
                                        versionId: version.versionId,
                                    })
                                })

                                promiseArray.push(
                                    SaveCcReviewComplianceTempAnswer(tempAnswer, lang).then(() => (
                                        SaveCcReviewComplianceTempAnswerVersions(tempVersions)
                                    )),
                                )
                            }
                        }

                        const nestedHistory = PrepareNestedHistory(itemHistory)

                        const importData: CcReviewComplianceHistoryImportProps = {
                            nestedHistory,
                            newRevisionId: newRevision.id,
                        }
                        promiseArray.push(
                            ImportCcReviewComplianceHistory(importData),
                            CreateCcReviewComplianceState(newState),
                        )
                    })

                    Promise.all(promiseArray).then(() => {
                        // reload data
                        LoadCcReviewComplianceRevisions(project.id, activeVersion.fileId, activeVersion.id, set.id)
                        .then((newRevisions) => {
                            const newLastRevision = newRevisions[newRevisions.length - 1]
                            dispatch(setCcReviewComplianceRevisionList(set.id, newRevisions))
                            dispatch(setCcReviewComplianceRevisionActive(activeVersion.id, set.id, newLastRevision))

                            LoadCcReviewComplianceRevisionData(newLastRevision, dispatch).then(() => {
                                dispatch(setCcReviewComplianceItemLoadState(IDLE))
                            })
                        })
                    })
                })
            })
        })
    }


    return (
        <SetDataWrapper onClick={(e) => e.stopPropagation()}>
            <SetName>{set.name}</SetName>

            <SelectDropdownStyled
                title={activeRevision ? activeRevision.name : ""}
                row="true"
            >
                {
                    revisions.map((revision) => (
                        <SelectDropdownItem
                            key={revision.id}
                            onClick={() => changeActiveRevision(revision)}
                        >
                            {revision.name}
                        </SelectDropdownItem>
                    ))
                }
            </SelectDropdownStyled>
            {
                editAccess && (
                    <>
                        <Button
                            row
                            onClick={() => setShowDelete(true)}
                        >
                            {lang.delete}
                        </Button>
                        <Button
                            row
                            variant="success"
                            onClick={() => {
                                setRevisionName(activeRevision.name)
                                setShowRename(true)
                            }}
                        >
                            {lang.rename}
                        </Button>
                        <Button
                            row
                            variant="success"
                            onClick={() => setShowRerun(true)}
                        >
                            {lang.rerun}
                        </Button>
                    </>
                )
            }

            <ConfirmWrapper
                show={showDelete}
                onClose={onCancelDelete}
            >
                <Confirm>
                    <ConfirmTitle>{activeRevision && activeRevision.name}</ConfirmTitle>
                    <ConfirmDescription>{lang.confirmDeleteRevision}</ConfirmDescription>
                    <ConfirmBottom>
                        <Button row onClick={onCancelDelete}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmDelete}>{lang.confirm}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>

            <ConfirmWrapper
                show={showRename}
                onClose={onCancelRename}
            >
                <Confirm>
                    <ConfirmTitle>{lang.renameRevision}</ConfirmTitle>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.name}</Label>
                                <Input type="text" value={revisionName} onChange={(e) => setRevisionName(e.target.value)} />
                                <Feedback>{revisionValidation && revisionValidation.name}</Feedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <ConfirmBottom>
                        <Button row onClick={onCancelRename}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmRename}>
                            {renameState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>

            <ConfirmWrapper
                show={showRerun}
                onClose={onCancelRerun}
            >
                <Confirm>
                    <ConfirmTitle>{lang.rerun}</ConfirmTitle>
                    <ConfirmTable>
                        <tbody>
                            <tr>
                                <td>
                                    <Button variant="success" onClick={() => onRerun(true)}>{lang.rerunAll}</Button>
                                </td>
                                <td>{lang.rerunAllDescription}</td>
                            </tr>
                            <tr>
                                <td>
                                    <Button variant="success" onClick={() => onRerun(false)}>{lang.rerunNoneCompliantItems}</Button>
                                </td>
                                <td>{lang.rerunNoneCompliantItemsDescription}</td>
                            </tr>
                        </tbody>
                    </ConfirmTable>

                    <ConfirmBottom>
                        <Button onClick={onCancelRerun}>{lang.cancel}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </SetDataWrapper>
    )
}
export default SetCustomView
